import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  Theme,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { IconBox } from "Components/IconBox";
import { CustomRectangleButton } from "Components/Form";
import { isValidString } from "Utils/validators";
import { useAppDispatch, useAppSelector } from "Hooks/reduxHook";
import { getAccessToken } from "Features/slice/customer/customer.Slice";
import { Invoices } from "Features/services/invoice.api";
import { SnackBarContext } from "Context/SnackContext";
import InvoiceDetailModal from "Screens/Invoice/InvoiceDetailModal";
import InfoIcon from "@mui/icons-material/Info";
import { revertStatus } from "Utils/statusFormat";

type StatusInterface = "In Process" | "Shipped" | "FullFilled" | "Cancelled";

interface ListInterface {
  id: string;
  date: string;
  order: {
    id: string;
    name: string;
    orderCount: number;
    quantity: number;
  };
  status?: StatusInterface;
  total: string;
}

const statusColorMapper = (
  status: "In Process" | "Shipped" | "FullFilled" | "Cancelled"
): string => {
  return {
    "In Process": "#2C107B",
    Shipped: "#FFAA2A",
    FullFilled: "#008319",
    Cancelled: "#FF0000",
  }[status];
};

export const OrderHistory = ({
  customerId,
  size,
  containerStyle,
  rowBgSelection,
}: {
  customerId: any;
  size: "medium" | "small";
  containerStyle?: SxProps<Theme> | undefined;
  rowBgSelection: "odd" | "even";
}) => {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.body}`]: {
      fontSize: 12,
    },
  }));

  const data =
    rowBgSelection === "odd" ? "&:nth-of-type(odd)" : "&:nth-of-type(even)";

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    [data]: {
      backgroundColor: "#007CA40D",
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const { handleSnackbar } = useContext(SnackBarContext);
  const [trackStatusUpdated, setTrackStatusUpdated] = useState<boolean>(false)

  const [orderHistory, setOrderHistory] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [invoiceDetail, setInvoiceDetail] = useState<any>();
  const [invoiceModal, setInvoiceModal] = useState<boolean>(false);

  const openInvoiceModal = () => {
    setInvoiceModal(true);
  };

  const closeInvoiceModal = () => {
    setInvoiceModal(false);
  };

  const dispatch = useAppDispatch();

  const { access_token } = useAppSelector((state) => state.customer);

  useEffect(() => {
    dispatch(getAccessToken());
  }, []);


  const fetchOrderHistory = async () => {
    try {
      setLoading(true);
      const payload = {
        access_token: access_token,
        customer_id: customerId,
      };
      const response = await Invoices.instance.getOrderHistory(payload);

      const data = response.data.data.data;
      setOrderHistory(data);
    } catch (error) {
      handleSnackbar(
        true,
        "An error occurred while fetching order history",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchOrderHistory();
    }
  }, [customerId]);

  const handleViewInvoice = async (invoiceId: string) => {
    const payload = {
      access_token,
      invoiceId,
    };

    try {
      setInvoiceModal(true);
      setLoading(true);
      const response = await Invoices.instance.invoiceDetail(payload);
      setInvoiceDetail(response.data);
    } catch (error: any) {
      handleSnackbar(
        true,
        error?.message || "An error occurred while searching invoice",
        "error"
      );
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "10vh",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : orderHistory && orderHistory.length > 0 ? (
        <TableContainer sx={containerStyle}>
          <Table aria-label="customized table" size={size}>
            <TableBody>
              {orderHistory.map((row: any) => (
                <StyledTableRow key={row?.Id}>
                  <StyledTableCell align="left">{row.TxnDate}</StyledTableCell>
                  <StyledTableCell align="left">
                    <Typography fontSize={"10px"}>
                      Order # {row.DocNumber}
                    </Typography>
                    <Typography fontSize={"10px"}>
                      {row.CustomerRef.name}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Typography fontSize={"10px"}>
                        Items Ordered: {row.Line.length}
                      </Typography>
                      <Typography fontSize={"10px"}>
                        Total Quantity:{" "}
                        {row.Line.reduce(
                          (acc: number, line: any) =>
                            acc + (line.SalesItemLineDetail?.Qty || 0),
                          0
                        )}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Typography fontSize={"10px"}>
                      ${row.Balance.toFixed(2)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Typography fontSize={"10px"}>
                      {revertStatus(row?.invoiceStatus) || "N/A"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left" className="clickable-text">
                    <IconBox
                      color="#FFAA2A"
                      icon={
                        <InfoIcon sx={{ color: "#FFAA2A", fontSize: "20px" }} />
                      }
                      containerStyle={{ padding: "4px" }}
                      onClick={() => handleViewInvoice(row.Id)}
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "10vh",
          }}
        >
          <Typography variant="h6">Order History doesn't exist</Typography>
        </Box>
      )}

      <InvoiceDetailModal
        invoiceModal={invoiceModal}
        closeInvoiceModal={closeInvoiceModal}
        invoiceDetail={invoiceDetail}
        setTrackStatusUpdated={setTrackStatusUpdated}
      />
    </>
  );
};
